import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface Connection {
  id: number;
  requester_company_id: number;
  requester_company_name: string;
  requester_company_slug: string;
  target_company_id: number;
  target_company_name: string;
  target_company_slug: string;
  status: string;
  message: string | null;
  created_at: string;
}

interface SearchCompany {
  id: number;
  name: string;
  slug: string;
  city: string | null;
  province: string | null;
}

export default function ConnectionsPage(): React.ReactElement {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'connected' | 'pending' | 'search'>('connected');
  const [searchQuery, setSearchQuery] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const modalRef = useRef<HTMLDialogElement>(null);

  const myCompanyId = user?.company_id;

  // All connections
  const { data: connectionsData } = useQuery({
    queryKey: ['connections'],
    queryFn: () => api.get<{ data: Connection[] }>('/v1/connections'),
  });

  const connections = connectionsData?.data || [];
  const accepted = connections.filter(c => c.status === 'accepted');
  const pending = connections.filter(c => c.status === 'pending');
  const pendingReceived = pending.filter(c => c.target_company_id === myCompanyId);
  const pendingSent = pending.filter(c => c.requester_company_id === myCompanyId);

  // Search
  const { data: searchData } = useQuery({
    queryKey: ['connections-search', searchQuery],
    queryFn: () => api.get<{ data: SearchCompany[] }>(`/v1/connections/search?q=${searchQuery}`),
    enabled: searchQuery.length >= 2,
  });

  const searchResults = searchData?.data || [];

  // Mutations
  const sendRequest = useMutation({
    mutationFn: (data: { target_company_id: number; message?: string }) =>
      api.post('/v1/connections/request', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      modalRef.current?.close();
      setRequestMessage('');
      setSelectedCompanyId(null);
    },
  });

  const acceptMutation = useMutation({
    mutationFn: (id: number) => api.patch(`/v1/connections/${id}/accept`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connections'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => api.patch(`/v1/connections/${id}/reject`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connections'] }),
  });

  function getPartnerName(conn: Connection): string {
    return conn.requester_company_id === myCompanyId
      ? conn.target_company_name
      : conn.requester_company_name;
  }

  function getPartnerSlug(conn: Connection): string {
    return conn.requester_company_id === myCompanyId
      ? conn.target_company_slug
      : conn.requester_company_slug;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('nav.connections')}</h1>

      {/* Tabs */}
      <div className="tabs tabs-boxed mb-6">
        <button className={`tab ${tab === 'connected' ? 'tab-active' : ''}`} onClick={() => setTab('connected')}>
          Conectados ({accepted.length})
        </button>
        <button className={`tab ${tab === 'pending' ? 'tab-active' : ''}`} onClick={() => setTab('pending')}>
          Pendientes ({pending.length})
        </button>
        <button className={`tab ${tab === 'search' ? 'tab-active' : ''}`} onClick={() => setTab('search')}>
          {t('buttons.search')}
        </button>
      </div>

      {/* Connected */}
      {tab === 'connected' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accepted.length === 0 && (
            <p className="text-base-content/50 col-span-full">No hay conexiones aún</p>
          )}
          {accepted.map(conn => (
            <div key={conn.id} className="card bg-base-100 shadow-sm border border-base-300">
              <div className="card-body">
                <h3 className="card-title text-lg">{getPartnerName(conn)}</h3>
                {conn.message && <p className="text-sm text-base-content/60">{conn.message}</p>}
                <div className="card-actions justify-end mt-2">
                  <a href={`/c/${getPartnerSlug(conn)}`} className="btn btn-sm btn-primary">
                    Ver catálogo
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending */}
      {tab === 'pending' && (
        <div className="space-y-6">
          {pendingReceived.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Solicitudes recibidas</h3>
              {pendingReceived.map(conn => (
                <div key={conn.id} className="card bg-base-100 shadow-sm border border-base-300 mb-3">
                  <div className="card-body flex-row items-center justify-between">
                    <div>
                      <p className="font-semibold">{conn.requester_company_name}</p>
                      {conn.message && <p className="text-sm text-base-content/60">{conn.message}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => acceptMutation.mutate(conn.id)}
                      >
                        Aceptar
                      </button>
                      <button
                        className="btn btn-sm btn-error btn-outline"
                        onClick={() => rejectMutation.mutate(conn.id)}
                      >
                        Rechazar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pendingSent.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Solicitudes enviadas</h3>
              {pendingSent.map(conn => (
                <div key={conn.id} className="card bg-base-100 shadow-sm border border-base-300 mb-3">
                  <div className="card-body flex-row items-center justify-between">
                    <p className="font-semibold">{conn.target_company_name}</p>
                    <span className="badge badge-warning">Pendiente</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pending.length === 0 && (
            <p className="text-base-content/50">No hay solicitudes pendientes</p>
          )}
        </div>
      )}

      {/* Search */}
      {tab === 'search' && (
        <div>
          <input
            type="text"
            placeholder="Buscar inmobiliarias..."
            className="input input-bordered w-full max-w-md mb-4"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />

          {searchQuery.length < 2 && (
            <p className="text-base-content/50">Escribí al menos 2 caracteres para buscar</p>
          )}

          <div className="space-y-2">
            {searchResults.map(company => (
              <div key={company.id} className="card bg-base-100 shadow-sm border border-base-300">
                <div className="card-body flex-row items-center justify-between py-3">
                  <div>
                    <p className="font-semibold">{company.name}</p>
                    <p className="text-sm text-base-content/60">
                      {[company.city, company.province].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                      setSelectedCompanyId(company.id);
                      modalRef.current?.showModal();
                    }}
                  >
                    Enviar solicitud
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send Request Modal */}
      <dialog ref={modalRef} className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">Enviar solicitud de conexión</h3>
          <textarea
            className="textarea textarea-bordered w-full"
            placeholder="Mensaje opcional..."
            value={requestMessage}
            onChange={e => setRequestMessage(e.target.value)}
            rows={3}
          />
          <div className="modal-action">
            <button className="btn btn-ghost" onClick={() => modalRef.current?.close()}>
              {t('buttons.cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (selectedCompanyId) {
                  sendRequest.mutate({
                    target_company_id: selectedCompanyId,
                    message: requestMessage || undefined,
                  });
                }
              }}
              disabled={sendRequest.isPending}
            >
              {sendRequest.isPending ? <span className="loading loading-spinner loading-sm" /> : 'Enviar'}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop"><button>close</button></form>
      </dialog>
    </div>
  );
}
